/**
 * File: apps/qbench-cp/reactapp/src/shared/utils/rbac.js
 *
 * Purpose: Validates a user's privilege list against an RBAC access policy.
 *
 * Scope: Customer portal per-customer privilege checks used by the RBAC access-control
 *     hooks/components.
 *
 * Tags: customer-portal, portal-roles
 *
 * Overview: `verifyPrivilege` intersects a user's privileges with a required privilege list
 *     and evaluates the result under an "any" (at least one match), "all" (every required
 *     privilege present), or "not" (no overlap) comparison mode; an empty required list is
 *     always considered sufficient. `verifyPolicy` reads a policy's `privilege`/`all`/`not`
 *     fields, picks the comparison mode accordingly, and returns false immediately if either
 *     the policy's privilege list or the user's privilege list is empty.
 *
 * Exports: verifyPolicy
 */

import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';

/**
 * Verify whether user privilege fulfill certain required permissions.
 * @param {string[]} userPrivilege
 * @param {string|string[]} requiredPrivilege
 * @param {'any'|'all'|'not'} comparison
 * @returns {boolean}
 */
function verifyPrivilege(
  userPrivilege = [],
  requiredPrivilege = [],
  comparison
) {
  let isSufficient = false;

  if (!requiredPrivilege || !requiredPrivilege.length) {
    isSufficient = true;
  } else {
    const validPermissions = intersection(userPrivilege, requiredPrivilege);

    switch (comparison) {
      case 'any':
        isSufficient = validPermissions.length >= 1; break;
      case 'all':
        isSufficient = validPermissions.length === requiredPrivilege.length; break;
      case 'not':
        isSufficient = validPermissions.length === 0; break;
      default:
        // Handle unknown comparison cases if needed
        isSufficient = false;
    }
  }

  return isSufficient;
}

/**
 * @typedef UserAccess
 * @prop {string} role Role ID from Auth0.
 */
/**
 * Validates user privilege according to the access policy
 * specified in `requiredPolicy`.
 * @param {string[]} userPrivilege Frontend privilege specific to user-customer.
 * @param {import('shared/hooks/misc/useAccessControl').AccessControl} requiredPolicy
 * @returns {boolean}
 */
export function verifyPolicy(userPrivilege, requiredPolicy) {
  const {
    privilege = [],
    all: checkAll = false,
    not: shouldInvert = false,
  } = requiredPolicy;

  if (isEmpty(privilege) || isEmpty(userPrivilege)) return false;

  // Check privilege
  const comparison = shouldInvert ? 'not' : checkAll ? 'all' : 'any';
  const privilegeSufficient = verifyPrivilege(
    userPrivilege,
    privilege,
    comparison // If privilege comparison is not provided, default to 'any'.
  );

  return privilegeSufficient;
}
